import type { RichVessel } from '../data/vesselEnrich';
import { extrapolate } from '../lib/geo';
import { vesselIconImageId } from './vesselIcons';

const PATH_LOOKAHEAD_SEC = 120;
const MAX_VESSELS_AT_LOW_ZOOM = 500;

function vesselSortKey(v: RichVessel): number {
  return v.dwt ?? v.length ?? 0;
}

export function capVesselsForZoom(vessels: RichVessel[], zoom: number): RichVessel[] {
  if (zoom >= 4 || vessels.length <= MAX_VESSELS_AT_LOW_ZOOM) return vessels;
  return [...vessels].sort((a, b) => vesselSortKey(b) - vesselSortKey(a)).slice(0, MAX_VESSELS_AT_LOW_ZOOM);
}

function extrapolatedPosition(v: RichVessel, dtSec: number) {
  if (v.lat == null || v.lon == null) return null;
  const track = v.course ?? v.heading;
  if (v.speed != null && track != null) {
    const next = extrapolate({ lat: v.lat, lon: v.lon, groundSpeed: v.speed, track }, dtSec);
    if (next) return next;
  }
  return { lat: v.lat, lon: v.lon };
}

export function buildVesselsGeoJSON(
  vessels: RichVessel[],
  dtSec: number,
  selectedId: string | null,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const v of vessels) {
    const pos = extrapolatedPosition(v, dtSec);
    if (!pos) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
      properties: {
        id: v.id,
        name: v.name,
        icon: v.iconKind,
        iconImage: vesselIconImageId(v.iconKind),
        course: v.course ?? v.heading ?? 0,
        speed: v.speed,
        selected: v.id === selectedId,
        sortKey: vesselSortKey(v),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function buildVesselPathsGeoJSON(
  vessels: RichVessel[],
  dtSec: number,
  selectedId: string | null,
): GeoJSON.FeatureCollection {
  if (!selectedId) return { type: 'FeatureCollection', features: [] };
  const v = vessels.find((x) => x.id === selectedId);
  if (!v) return { type: 'FeatureCollection', features: [] };
  const start = extrapolatedPosition(v, dtSec);
  if (!start || v.speed == null || (v.course ?? v.heading) == null) {
    return { type: 'FeatureCollection', features: [] };
  }
  const end = extrapolate(
    { lat: start.lat, lon: start.lon, groundSpeed: v.speed, track: v.course ?? v.heading! },
    PATH_LOOKAHEAD_SEC,
  );
  if (!end) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [[start.lon, start.lat], [end.lon, end.lat]] },
        properties: { id: v.id, icon: v.iconKind, selected: true },
      },
    ],
  };
}

export interface VesselTrailPoint {
  lat: number;
  lon: number;
  t: number;
}

const TRAIL_MIN_DIST_M = 80;
const TRAIL_MAX_POINTS = 1600;

function distM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function appendVesselTrail(
  trail: VesselTrailPoint[],
  lat: number,
  lon: number,
  t: number,
): VesselTrailPoint[] {
  const last = trail[trail.length - 1];
  if (last && distM(last, { lat, lon }) < TRAIL_MIN_DIST_M) return trail;
  return [...trail, { lat, lon, t }].slice(-TRAIL_MAX_POINTS);
}

export function vesselTrailToGeoJSON(trail: VesselTrailPoint[]): GeoJSON.FeatureCollection {
  if (trail.length < 2) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: trail.map((p) => [p.lon, p.lat]) },
      },
    ],
  };
}

export function buildVesselLabelsGeoJSON(
  vessels: RichVessel[],
  dtSec: number,
  selectedId: string | null,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const v of vessels) {
    if (v.id !== selectedId && !v.name) continue;
    const pos = extrapolatedPosition(v, dtSec);
    if (!pos) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
      properties: {
        id: v.id,
        label: v.name ?? v.mmsi ?? v.id,
        selected: v.id === selectedId,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}
